// src/routes/DeviceRouteGuard.jsx
import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

const DESKTOP_LANDING_BREAKPOINT = 440;

function isTrueMobileDevice() {
  if (typeof navigator === 'undefined') return false;

  const ua = navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|ipod|mobile|tablet/i.test(ua);
}

function getIsDesktopLandingWidth() {
  if (typeof window === 'undefined') return false;

  return window.innerWidth > DESKTOP_LANDING_BREAKPOINT && !isTrueMobileDevice();
}

function isPublicLegalPath(pathname) {
  const prefixes = ['/terms', '/privacy'];

  return prefixes.some(
    (p) => pathname === p || pathname.startsWith(p + '/')
  );
}

function isHostPath(pathname) {
  return pathname === '/host' || pathname.startsWith('/host/');
}

function isRecruitingPath(pathname) {
  return (
    pathname === '/host-recruiting' ||
    pathname.startsWith('/host-recruiting/')
  );
}

function isReferralPartnerPath(pathname) {
  return (
    pathname === '/partner' ||
    pathname.startsWith('/partner/') ||
    pathname === '/referral-partner' ||
    pathname.startsWith('/referral-partner/')
  );
}

function isPortalPath(pathname) {
  return pathname === '/portal' || pathname.startsWith('/portal/');
}

function isDesktopAllowedPath(pathname) {
  return (
    pathname === '/events' ||
    pathname === '/desktoplanding' ||
    pathname.startsWith('/admin') ||
    isPublicLegalPath(pathname) ||
    isHostPath(pathname) ||
    isRecruitingPath(pathname) ||
    isReferralPartnerPath(pathname) ||
    isPortalPath(pathname)
  );
}

export default function DeviceRouteGuard() {
  const location = useLocation();
  const pathname = location.pathname;

  const [isDesktopLandingWidth, setIsDesktopLandingWidth] = useState(
    getIsDesktopLandingWidth
  );

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopLandingWidth(getIsDesktopLandingWidth());
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const isAdmin = pathname.startsWith('/admin');
  const isLegal = isPublicLegalPath(pathname);
  const isHost = isHostPath(pathname);
  const isRecruiting = isRecruitingPath(pathname);
  const isReferralPartner = isReferralPartnerPath(pathname);
  const isPortal = isPortalPath(pathname);

  if (isLegal) {
    return <Outlet />;
  }

  if (isAdmin) {
    return <Outlet />;
  }

  if (isRecruiting) {
    return <Outlet />;
  }

  // Logged-in portal selector.
  if (isPortal) {
    return <Outlet />;
  }

  // Referral Partner routes.
  if (isReferralPartner) {
    return <Outlet />;
  }

  // Host routes.
  if (isHost) {
    if (isDesktopLandingWidth && pathname === '/host') {
      return <Navigate to="/host/portal" replace />;
    }

    return <Outlet />;
  }

  if (pathname === '/') {
    return <Navigate to="/events" replace />;
  }

  if (pathname === '/desktoplanding') {
    return <Navigate to="/events" replace />;
  }

  if (isDesktopLandingWidth && !isDesktopAllowedPath(pathname)) {
    return <Navigate to="/events" replace />;
  }

  return <Outlet />;
}